import { useMonaco } from '@monaco-editor/react';
import { useEffect, useMemo } from 'react';

import { AthenaTableGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  monacoEditorSqlKeywords,
  monacoEditorTrinoSqlFunctions,
} from 'packages/athena/hooks/consts';
import { setupMonacoEditorHelpers } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/common';

const createAthenaSchemaProposals = ({
  athenaTables,
}: {
  athenaTables: AthenaTableGQLType[];
}) => {
  if (!athenaTables || !athenaTables.length) {
    return [];
  }

  const proposalsMap: {
    [label: string]: {
      label: string;
      documentation: string;
      insertText: string;
      detail: string;
    };
  } = {};

  athenaTables.forEach(({ tableName, properties }) => {
    proposalsMap[tableName] = {
      label: tableName,
      documentation: 'Name of the table',
      insertText: tableName,
      detail: 'Table',
    };

    // have to dedupe table property name suggestions because many tables could have the same field names
    if (properties.length) {
      properties.forEach(({ name: propertyName, type: propertyType }) => {
        if (!proposalsMap[propertyName]) {
          proposalsMap[propertyName] = {
            label: propertyName,
            documentation: `Field type: ${propertyType}`,
            insertText: propertyName,
            detail: `${propertyName} (${propertyType})`,
          };
        }
      });
    }
  });

  return Object.values(proposalsMap);
};

export const useAthenaMonacoEditor = ({
  athenaTables,
}: {
  athenaTables: AthenaTableGQLType[];
}) => {
  const monaco = useMonaco();

  const athenaTableNameProposals = useMemo(
    () => createAthenaSchemaProposals({ athenaTables }),
    [athenaTables],
  );

  useEffect(() => {
    if (monaco) {
      const {
        addMonacoEditorVariables,
        addMonacoEditorFunctions,
      } = setupMonacoEditorHelpers(monaco);

      const provider = monaco.languages.registerCompletionItemProvider('sql', {
        provideCompletionItems(model, position) {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const athenaTableNameSuggestions = addMonacoEditorVariables(
            athenaTableNameProposals,
            { range },
          );

          const keywordSuggestions = addMonacoEditorFunctions(
            monacoEditorSqlKeywords,
            { range, kind: monaco.languages.CompletionItemKind.Keyword },
          );

          const builtinFunctionSuggestions = addMonacoEditorFunctions(
            monacoEditorTrinoSqlFunctions,
            { range, kind: monaco.languages.CompletionItemKind.Function },
          );

          return {
            suggestions: [
              ...athenaTableNameSuggestions,
              ...keywordSuggestions,
              ...builtinFunctionSuggestions,
            ],
          };
        },
      });

      return () => {
        provider.dispose();
      };
    }

    return () => {};
  }, [monaco, athenaTableNameProposals]);
};
